import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { formatConversationMode } from '../utils/format';
import type { Conversation } from '../types';

export type ConversationTableProps = {
  conversations: Conversation[];
  showAppColumn?: boolean;
  appLabelFor?: (appId: string) => string;
  linkFor?: (conversation: Conversation) => string;
  onDelete?: (conversationId: string) => void;
  onArchive?: (conversationId: string) => void;
  onUnarchive?: (conversationId: string) => void;
  canManageConversation?: (conversation: Conversation) => boolean;
};

export function ConversationTable({
  conversations,
  showAppColumn,
  appLabelFor,
  linkFor,
  onDelete,
  onArchive,
  onUnarchive,
  canManageConversation
}: ConversationTableProps) {
  const navigate = useNavigate();
  const resolveLink =
    linkFor ?? ((conversation: Conversation) => `/conversations/${conversation.id}`);
  const showApp = showAppColumn ?? false;
  const colSpan = showApp ? 9 : 8;

  const pipelineStatusClass = (status?: string | null) => {
    if (!status) {
      return 'badge';
    }
    const normalized = status.toLowerCase();
    if (['failed', 'canceled', 'skipped'].includes(normalized)) {
      return 'badge badge--error';
    }
    if (['success'].includes(normalized)) {
      return 'badge badge--success';
    }
    if (['running', 'pending', 'created', 'manual'].includes(normalized)) {
      return 'badge badge--warning';
    }
    return 'badge';
  };

  const mrStatusClass = (status?: string | null) => {
    if (!status) {
      return 'badge';
    }
    const normalized = status.toLowerCase();
    if (['merged'].includes(normalized)) {
      return 'badge badge--success';
    }
    if (['closed'].includes(normalized)) {
      return 'badge badge--error';
    }
    if (['opened', 'open', 'locked'].includes(normalized)) {
      return 'badge badge--warning';
    }
    return 'badge';
  };

  return (
    <div className="table-scroll responsive-table-scroll">
      <table className="responsive-table conversation-table">
        <thead>
          <tr>
            <th>Title</th>
            {showApp && <th>App</th>}
            <th>Model</th>
            <th>Scope</th>
            <th>Status</th>
            <th>MR</th>
            <th>Build</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {conversations.map((conversation) => {
            const link = resolveLink(conversation);
            const mr = conversation.gitlab?.merge_request ?? null;
            const pipeline = conversation.gitlab?.latest_pipeline ?? null;
            const mrState = mr?.state ?? null;
            const pipelineStatus = pipeline?.status ?? null;
            // Omit destructive lifecycle controls until the resource-scoped
            // access lookup has positively established conversation:manage.
            const canManage = canManageConversation?.(conversation) ?? true;
            return (
              <tr
                key={conversation.id}
                className="table-row-link"
                role="button"
                tabIndex={0}
                onClick={() => navigate(link)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(link);
                  }
                }}
              >
                <td data-label="Title" className="conversation-table__title">
                  {conversation.title ?? 'Untitled'}
                </td>
                {showApp && (
                  <td data-label="App">
                    {appLabelFor ? appLabelFor(conversation.app_id) : conversation.app_id}
                  </td>
                )}
                <td data-label="Model">{conversation.model_name ?? '-'}</td>
                <td data-label="Scope">
                  <span className="badge">{formatConversationMode(conversation.mode)}</span>
                </td>
                <td data-label="Status">{conversation.status}</td>
                <td data-label="MR">
                  {mr ? (
                    <div className="row">
                      <a
                        href={mr.web_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        !{mr.iid}
                      </a>
                      <span className={mrStatusClass(mrState)}>{mrState ?? 'unknown'}</span>
                    </div>
                  ) : (
                    <span className="text-muted">
                      {conversation.gitlab?.branch_exists ? 'No MR' : '—'}
                    </span>
                  )}
                </td>
                <td data-label="Build">
                  {pipeline ? (
                    <a
                      href={pipeline.web_url}
                      target="_blank"
                      rel="noreferrer"
                      className={pipelineStatusClass(pipelineStatus)}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {pipelineStatus ?? 'unknown'}
                    </a>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td data-label="Updated">{new Date(conversation.updated_at).toLocaleString()}</td>
                <td data-label="Actions" className="responsive-table__actions">
                  <div className="row">
                    <Link to={link} onClick={(event) => event.stopPropagation()}>
                      Open
                    </Link>
                    {canManage && onArchive && !conversation.archived_at && (
                      <button
                        type="button"
                        className="ghost compact"
                        onClick={(event) => {
                          event.stopPropagation();
                          onArchive(conversation.id);
                        }}
                      >
                        Archive
                      </button>
                    )}
                    {canManage && onUnarchive && conversation.archived_at && (
                      <button
                        type="button"
                        className="ghost compact"
                        onClick={(event) => {
                          event.stopPropagation();
                          onUnarchive(conversation.id);
                        }}
                      >
                        Unarchive
                      </button>
                    )}
                    {canManage && onDelete && (
                      <button
                        type="button"
                        className="ghost compact"
                        onClick={(event) => {
                          event.stopPropagation();
                          onDelete(conversation.id);
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {conversations.length === 0 && (
            <tr className="responsive-table__empty">
              <td colSpan={colSpan}>No conversations yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
